import { FilterValues } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { translateString } from '@src/util/intl/translateString';

import {
  AGREEMENT_SUBTYPE,
  AGREEMENT_SUBTYPE_SEND_SCOPE,
  DOCUMENT_REQUEST_KIND,
  DOCUMENT_REQUEST_STATUS,
  OVERTIME_COMPENSATION_TYPE,
  OVERTIME_CONFIRMED_HOURS_MAX,
  OVERTIME_REST_HOLIDAY_HOURS_FACTOR,
  getAgreementSubtypeDefinition as getAgreementSubtypeDefinitionFromDefinitions,
  getMaxSelectionsForAgreementSubtype as getMaxSelectionsForAgreementSubtypeFromDefinitions,
  isAgreementSubtypeIndividualScope as isAgreementSubtypeIndividualScopeFromDefinitions,
  isUploadBasedDocumentKind,
  type AgreementSubtype,
  type AgreementSubtypeCardDefinition,
  type AgreementSubtypeSendScope,
  type DocumentRequestKind,
  type DocumentRequestStatus,
  type OvertimeCompensationTypeValue,
} from '@src/models/interfaces/documentRequest';

export {
  AGREEMENT_SUBTYPE,
  AGREEMENT_SUBTYPE_SEND_SCOPE,
  DOCUMENT_REQUEST_KIND,
  DOCUMENT_REQUEST_STATUS,
  OVERTIME_COMPENSATION_TYPE,
  OVERTIME_CONFIRMED_HOURS_MAX,
  OVERTIME_REST_HOLIDAY_HOURS_FACTOR,
  isUploadBasedDocumentKind,
};

export type {
  AgreementSubtype,
  AgreementSubtypeCardDefinition,
  AgreementSubtypeSendScope,
  DocumentRequestKind,
  DocumentRequestStatus,
  OvertimeCompensationTypeValue,
};

const BASE_URL = 'document-requests';

export const DOCUMENT_LIST_TAB = {
  DOCUMENTS: 'documents',
  WORKER_REQUESTS: 'workerRequests',
} as const;

export type DocumentListTab = (typeof DOCUMENT_LIST_TAB)[keyof typeof DOCUMENT_LIST_TAB];

const DOCUMENT_REQUEST_DETAIL_RELATIVE = ':documentRequestId';
const DOCUMENT_REQUEST_REVIEW_RELATIVE = ':documentRequestId/review';

export const PATHS = {
  BASE_URL,
  CREATE_DOCUMENT_REQUEST: `${BASE_URL}/create`,
  EDIT_DOCUMENT_REQUEST: `${BASE_URL}/edit/:id`,
  DOCUMENT_REQUEST_DETAIL: `${BASE_URL}/${DOCUMENT_REQUEST_DETAIL_RELATIVE}`,
  DOCUMENT_REQUEST_DETAIL_RELATIVE,
  DOCUMENT_REQUEST_REVIEW: `${BASE_URL}/${DOCUMENT_REQUEST_REVIEW_RELATIVE}`,
  DOCUMENT_REQUEST_REVIEW_RELATIVE,
};

export const buildDocumentRequestDetailPath = (documentRequestId: string) =>
  `${BASE_URL}/${documentRequestId}`;

export const getRouteToDocumentRequestDetail = (documentRequestId: string) => documentRequestId;

export const getRouteToDocumentRequestReview = (documentRequestId: string) =>
  `${documentRequestId}/review`;

export const getToDocumentRequestList = (orgId: string) => `/${orgId}/-/${BASE_URL}`;

/** Fixed Storage object name for user-imported PDF on document request create. */
export const OFFICIAL_SOURCE_PDF_FILENAME = 'source.pdf';

/** Storage segment for client uploads before create callable (backend persists same path). */
export const OFFICIAL_SOURCE_PDF_UPLOADS_SEGMENT = 'uploads';

/** Max size for imported official PDF (10 MB). */
export const OFFICIAL_SOURCE_PDF_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Relative Storage path for pre-create PDF upload (no leading slash, no gs://).
 * uploadId is a client session id — not the Firestore documentRequestId.
 */
export function buildOfficialSourcePdfUploadPath(
  organizationId: string,
  uploadId: string,
): string {
  return `organizations/${organizationId}/document_requests/${OFFICIAL_SOURCE_PDF_UPLOADS_SEGMENT}/${uploadId}/${OFFICIAL_SOURCE_PDF_FILENAME}`;
}

export type DocumentTypeCardConfig = {
  kind: DocumentRequestKind;
  titleId: string;
  subtitleId: string;
  descriptionId: string;
  iconName: string;
};

export const AGREEMENT_SUBTYPE_CARD_DEFINITIONS: AgreementSubtypeCardDefinition[] = [
  {
    value: AGREEMENT_SUBTYPE.SHIFT_CHANGE,
    scopeDelivery: AGREEMENT_SUBTYPE_SEND_SCOPE.MULTIPLE,
    titleId: 'documents.flow.agreement.subtype.shiftChange',
    descriptionId: 'documents.flow.agreement.subtype.shiftChange.description',
  },
  {
    value: AGREEMENT_SUBTYPE.WORKJOURNEY_CHANGE,
    scopeDelivery: AGREEMENT_SUBTYPE_SEND_SCOPE.MULTIPLE,
    titleId: 'documents.flow.agreement.subtype.workJourneyChange',
    descriptionId: 'documents.flow.agreement.subtype.workJourneyChange.description',
  },
  {
    value: AGREEMENT_SUBTYPE.OVERTIME,
    scopeDelivery: AGREEMENT_SUBTYPE_SEND_SCOPE.INDIVIDUAL,
    titleId: 'documents.flow.agreement.subtype.extraHoursAgreement',
    descriptionId: 'documents.flow.agreement.subtype.extraHoursAgreement.description',
  },
  {
    value: AGREEMENT_SUBTYPE.OVERTIME_COMPENSATION,
    scopeDelivery: AGREEMENT_SUBTYPE_SEND_SCOPE.INDIVIDUAL,
    titleId: 'documents.flow.agreement.subtype.extraHoursCompensationRest',
    descriptionId: 'documents.flow.agreement.subtype.extraHoursCompensationRest.description',
  },
];

/** Short labels for filters and summaries (same titles as cards). */
export const AGREEMENT_SUBTYPE_OPTIONS: { value: AgreementSubtype; labelId: string }[] =
  AGREEMENT_SUBTYPE_CARD_DEFINITIONS.map(({ value, titleId }) => ({ value, labelId: titleId }));

export function getAgreementSubtypeDefinition(
  subType: AgreementSubtype | undefined,
): AgreementSubtypeCardDefinition | undefined {
  return getAgreementSubtypeDefinitionFromDefinitions(subType, AGREEMENT_SUBTYPE_CARD_DEFINITIONS);
}

export function getMaxSelectionsForAgreementSubtype(
  subType: AgreementSubtype | undefined,
): number | undefined {
  return getMaxSelectionsForAgreementSubtypeFromDefinitions(subType, AGREEMENT_SUBTYPE_CARD_DEFINITIONS);
}

/** True when the subtype is configured for a single recipient (matches card scope). */
export function isAgreementSubtypeIndividualScope(subType: AgreementSubtype | undefined): boolean {
  return isAgreementSubtypeIndividualScopeFromDefinitions(subType, AGREEMENT_SUBTYPE_CARD_DEFINITIONS);
}

export const DOCUMENT_TYPE_CARDS: DocumentTypeCardConfig[] = [
  {
    kind: DOCUMENT_REQUEST_KIND.NOTIFICATION,
    titleId: 'documents.flow.type.notification.title',
    subtitleId: 'documents.flow.type.notification.subtitle',
    descriptionId: 'documents.flow.type.notification.description',
    iconName: 'notifications',
  },
  {
    kind: DOCUMENT_REQUEST_KIND.AGREEMENT,
    titleId: 'documents.flow.type.agreement.title',
    subtitleId: 'documents.flow.type.agreement.subtitle',
    descriptionId: 'documents.flow.type.agreement.description',
    iconName: 'groups',
  },
  {
    kind: DOCUMENT_REQUEST_KIND.CONTRACT_APPENDIX,
    titleId: 'documents.flow.type.contractAppendix.title',
    subtitleId: 'documents.flow.type.contractAppendix.subtitle',
    descriptionId: 'documents.flow.type.contractAppendix.description',
    iconName: 'article',
  },
];

export const INIT_DOCUMENT_LIST_FILTERS: FilterValues = {
  targetUser: {
    value: '',
    label: translateString('documents.list.filter.worker'),
    valueType: 'string',
  },
  status: {
    value: '',
    label: translateString('documents.list.filter.status'),
    valueType: 'string',
  },
  documentType: {
    value: '',
    label: translateString('documents.list.filter.documentClass'),
    valueType: 'string',
  },
  documentSubtype: {
    value: '',
    label: translateString('documents.list.filter.documentSubtype'),
    valueType: 'string',
  },
};
